// Assume you’ve imported your median income and homestead exemption data:
import medianIncomeData from './json/median_income.json';
import exemptionData from './json/all_exemptions.json';
import { computeChapter13Plan } from './calculators/chapter13Calculator'
import { computeDisposableIncome } from './calculators/disposableIncomeCalculator'
import {
  getChapter13TotalAttorneyFee,
  resolveAttorneyFeeSettings,
} from './utils/attorneyFeeSettings'

const stateAbbreviations = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming'
  };
  
const getStateName = (abbreviation) => {
return stateAbbreviations[abbreviation.toUpperCase()] || abbreviation;
};

// Helper: Check discharge requirements.
// (Assumes answers.bankruptcyChapter is "7" or "13", and bankruptcyYearsAgo is a numeric string.)
const checkDischargeEligibility = (answers) => {
  // For respondent
  if (answers.bankruptcyFiled === "yes") {
    if (answers.bankruptcyChapter === "7" && Number(answers.bankruptcyYearsAgo) < 8) {
      return false;
    }
    if (answers.bankruptcyChapter === "13" && Number(answers.bankruptcyYearsAgo) < 6) {
      return false;
    }
  }
  // For spouse (if filing jointly)
  if (answers.maritalStatus === "married" && answers.spouseFiling === "yes" && answers.spouseBankruptcyFiled === "yes") {
    if (answers.spouseBankruptcyChapter === "7" && Number(answers.spouseBankruptcyYearsAgo) < 8) {
      return false;
    }
    if (answers.spouseBankruptcyChapter === "13" && Number(answers.spouseBankruptcyYearsAgo) < 6) {
      return false;
    }
  }
  return true;
};
// Helper function to get the six-month period (as Date objects)
function getSixMonthPeriod() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed: January is 0, April is 3, etc.
  
    // If current month is less than 6, then the period starts in the previous year.
    let startYear, startMonth;
    if (currentMonth < 6) {
      startYear = currentYear - 1;
      startMonth = currentMonth + 6; // e.g. if currentMonth is 3 (April), then 3+6=9 which is October (0-indexed)
    } else {
      startYear = currentYear;
      startMonth = currentMonth - 6;
    }
    const periodStart = new Date(startYear, startMonth, 1);
  
    // The period ends on the last day of the month preceding the current month.
    let endYear, endMonth;
    if (currentMonth === 0) { // if January, previous month is December of previous year
      endYear = currentYear - 1;
      endMonth = 11;
    } else {
      endYear = currentYear;
      endMonth = currentMonth - 1;
    }
    // new Date(year, month+1, 0) returns the last day of the given month.
    const periodEnd = new Date(endYear, endMonth + 1, 0);
    
    return { start: periodStart, end: periodEnd };
  }
  
  // Compute income for a current or second job.
  // job: an object with properties: jobDuration, startDate, payType, hourlyRate, hoursWorked, yearlySalary, netProfit.
  function computeJobIncome(job, periodStart, periodEnd) {
    let fraction = 1;
    if (job.jobDuration === "<1 yr" && job.startDate) {
      const jobStart = new Date(job.startDate);
      const activeStart = jobStart > periodStart ? jobStart : periodStart;
      const activeDays = (periodEnd - activeStart) / (1000 * 60 * 60 * 24) + 1;
      const totalDays = (periodEnd - periodStart) / (1000 * 60 * 60 * 24) + 1;
      fraction = activeDays / totalDays;
    }

    //console.log(job.yearlySalary)
    
    if (job.payType === "hourly") {
      const weeks = (periodEnd - periodStart) / (1000 * 60 * 60 * 24 * 7);
      // Calculate the number of weeks in the period.
      if (Number(job.hoursWorked) > 40){
        let hourlyPayWithOT = 0;
        hourlyPayWithOT += Number(job.hourlyRate) * Number(job.hoursWorked) * weeks * fraction / 6;
        hourlyPayWithOT += Number(job.hourlyRate) * Number(job.hoursWorked - 40) * weeks * fraction * 1.5 / 6;
        return hourlyPayWithOT;
      }
      
      return Number(job.hourlyRate) * Number(job.hoursWorked) * weeks * fraction / 6;
    }
    if (job.payType === "salary") {
      return (Number(job.yearlySalary) / 12) * fraction;
    }
    if (job.payType === "self-employed") {
      return Number(job.netProfit) * fraction;
    }
    return 0;
  }
  
  // Compute income for a prior job (which has a start and a leaving date).
  // Expects priorStartDate, priorLeavingDate, priorPayType, and the appropriate numeric fields.
  function computePriorJobIncome(job, periodStart, periodEnd) {
    let start = periodStart
    if (job.priorStartDate){
        start = new Date(job.priorStartDate);
    }
    const end = new Date(job.priorLeavingDate);
    const activeStart = start > periodStart ? start : periodStart;
    const activeEnd = end < periodEnd ? end : periodEnd;
    if (activeEnd < activeStart) return 0;
    const activeDays = (activeEnd - activeStart) / (1000 * 60 * 60 * 24) + 1;
    const totalDays = (periodEnd - periodStart) / (1000 * 60 * 60 * 24) + 1;
    const fraction = activeDays / totalDays;
    
    if (job.priorPayType === "hourly") {
      const weeks = (periodEnd - periodStart) / (1000 * 60 * 60 * 24 * 7);
      if (Number(job.priorHoursWorked) > 40){
        let hourlyPayWithOT = 0;
        hourlyPayWithOT += Number(job.priorHourlyRate) * Number(job.priorHoursWorked) * weeks * fraction / 6;
        hourlyPayWithOT += Number(job.priorHourlyRate) * Number(job.priorHoursWorked - 40) * weeks * fraction * 1.5 / 6;
        return hourlyPayWithOT;
      }
      return Number(job.priorHourlyRate) * Number(job.priorHoursWorked) * weeks * fraction / 6;
    }
    if (job.priorPayType === "salary") {
      return (Number(job.priorYearlySalary) / 12) * fraction;
    }
    if (job.priorPayType === "self-employed") {
      return Number(job.priorNetProfit) * fraction;
    }
    return 0;
  }
  
  // Now combine everything to compute the total monthly income.
  // This function looks at respondent's and spouse's current, second, and prior employment,
  // as well as fixed income sources (excluding Social Security and Veterans Benefits).
  function computeTotalMonthlyIncome(answers) {
    const { start, end } = getSixMonthPeriod();
    let primaryIncome = 0;
    let secondIncome = 0;
    let priorIncome = 0;
    let fixedIncome = 0
    let spousePrimaryIncome = 0;
    let spouseSecondIncome = 0;
    let spousePriorIncome = 0;
    let spouseFixedIncome = 0
    let totalIncome = 0;
    
    // Respondent Current Employment
    if (answers.employmentStatus === "yes") {
        primaryIncome = computeJobIncome(answers, start, end);
      totalIncome += primaryIncome;
    }
    
    // Respondent Second Job
    if (answers.secondJob === "yes") {
      secondIncome = computeJobIncome({
        jobDuration: answers.secondJobDuration,
        startDate: answers.secondStartDate,
        payType: answers.secondPayType,
        hourlyRate: answers.secondHourlyRate,
        hoursWorked: answers.secondHoursWorked,
        yearlySalary: answers.secondYearlySalary,
        netProfit: answers.secondNetProfit
      }, start, end);
      totalIncome += secondIncome;
    }
    
    // Respondent Prior Employment
    if (answers.priorEmployment === "yes") {
        priorIncome = computePriorJobIncome({
        priorStartDate: answers.priorStartDate,
        priorLeavingDate: answers.priorLeavingDate,
        priorPayType: answers.priorPayType,
        priorHourlyRate: answers.priorHourlyRate,
        priorHoursWorked: answers.priorHoursWorked,
        priorYearlySalary: answers.priorYearlySalary,
        priorNetProfit: answers.priorNetProfit
      }, start, end);
      totalIncome += priorIncome;
    }
    
    // Spouse Current Employment (if applicable)
    if (answers.maritalStatus === "married" && answers.spouseEmploymentStatus === "yes") {
      spousePrimaryIncome = computeJobIncome({
        jobDuration: answers.spouseJobDuration,
        startDate: answers.spouseStartDate,
        payType: answers.spousePayType,
        hourlyRate: answers.spouseHourlyRate,
        hoursWorked: answers.spouseHoursWorked,
        yearlySalary: answers.spouseYearlySalary,
        netProfit: answers.spouseNetProfit
      }, start, end);
      totalIncome += spousePrimaryIncome;
    }
    
    // Spouse Second Job
    if (answers.maritalStatus === "married" &&  answers.spouseSecondJob === "yes") {
      spouseSecondIncome = computeJobIncome({
        jobDuration: answers.spouseSecondJobDuration,
        startDate: answers.spouseSecondStartDate,
        payType: answers.spouseSecondPayType,
        hourlyRate: answers.spouseSecondHourlyRate,
        hoursWorked: answers.spouseSecondHoursWorked,
        yearlySalary: answers.spouseSecondYearlySalary,
        netProfit: answers.spouseSecondNetProfit
      }, start, end);
      totalIncome += spouseSecondIncome;
    }
    
    // Spouse Prior Employment
    if (answers.maritalStatus === "married" && answers.spousePriorEmployment === "yes") {
      spousePriorIncome = computePriorJobIncome({
        priorStartDate: answers.spousePriorStartDate,
        priorLeavingDate: answers.spousePriorLeavingDate,
        priorPayType: answers.spousePriorPayType,
        priorHourlyRate: answers.spousePriorHourlyRate,
        priorHoursWorked: answers.spousePriorHoursWorked,
        priorYearlySalary: answers.spousePriorYearlySalary,
        priorNetProfit: answers.spousePriorNetProfit
      }, start, end);
      totalIncome += spousePriorIncome;
    }
    
    // Add fixed income sources (only those that count; exclude Social Security and Veterans Benefits)
    for (const source in answers.fixedIncomeDetails) {
      if (source !== "Social Security" && source !== "Veterans Benefits") {
        fixedIncome += Number(answers.fixedIncomeDetails[source]) || 0;
        totalIncome += Number(answers.fixedIncomeDetails[source]) || 0;
      }
    }
    // Spouse fixed income sources
    if (answers.maritalStatus === "married") {
      for (const source in answers.spouseFixedIncomeDetails) {
        if (source !== "Social Security" && source !== "Veterans Benefits") {
            spouseFixedIncome +=Number(answers.spouseFixedIncomeDetails[source]) || 0;
          totalIncome += Number(answers.spouseFixedIncomeDetails[source]) || 0;
        }
      }
    }
    const incomeSources = {
        "primaryIncome":primaryIncome,
        "secondIncome":secondIncome,
        "priorIncome":priorIncome,
        "fixedIncome":fixedIncome,
        "spousePrimaryIncome":spousePrimaryIncome,
        "spouseSecondIncome":spouseSecondIncome,
        "spousePriorIncome":spousePriorIncome,
        "spouseFixedIncome":spouseFixedIncome,
        "totalIncome":totalIncome
    }
    return incomeSources;
  }
  
  // Example usage:
  // Assume `answers` is your state object that contains all the employment fields.
  // const monthlyIncome = computeTotalMonthlyIncome(answers);
  // console.log("Computed average monthly income:", monthlyIncome);

// Helper: Compare income against median thresholds.
function getMedianIncome(state, householdSize, medianIncomeData) {
    // Normalize the state string (e.g., "california" becomes "California")
    const normalizedState = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
    if (medianIncomeData[normalizedState] && medianIncomeData[normalizedState][String(householdSize)]) {
      return medianIncomeData[normalizedState][String(householdSize)];
    }
    return null;
  }
// (Assumes medianIncomeData is an object keyed by household size.)
const passesMeansTest = (answers) => {
  // Household size: respondent + (spouse if filing) + dependents.
  let householdSize = 1;
  if (answers.maritalStatus === "married") {
    householdSize++;
  }
  // Add one for each dependent in the dependentDetails array
  householdSize += Array.isArray(answers.dependentDetails)
    ? answers.dependentDetails.length
    : 0;
  const incomeTotals = computeTotalMonthlyIncome(answers);
  //console.log(incomeTotals)
  const annualIncome = incomeTotals.totalIncome;
  const normalizedState = getStateName(answers.state);
  // Get the median annual income threshold for this household size.
  const medianIncome = getMedianIncome(normalizedState, householdSize, medianIncomeData);

  // Compute annual income from monthly income.

  return { medianIncome, incomeTotals, householdSize };
};
function getHomesteadExemption(exemptions, stateAbbr, maritalStatus, homeOwnershipType) {
  // Determine joint and sole homestead limits for a state
  const key = stateAbbr.toUpperCase();
  const stateList = exemptions[key] || [];
  // Filter for homestead exemptions (petition_section "1" and "homestead" in asset_name)
  const homesteads = stateList.filter(
    e => String(e.petition_section) === '1' && /homestead/i.test(e.asset_name)
  );
  if (homesteads.length === 0) return 0;
  // Extract numeric limits and sort descending
  const limits = homesteads.map(e => parseFloat(e.limit) || 0).sort((a, b) => b - a);
  const joint = limits[0];
  let sole;
  if (limits.length === 1) {
    sole = joint;
  } else {
    const second = limits[1];
    sole = second >= joint / 2 ? second : joint;
  }
  // Married & co-owned uses joint limit, otherwise sole limit
  if (maritalStatus === 'married' && (homeOwnershipType === 'we own together' || homeOwnershipType === 'TxE')) {
    return joint;
  }
  return sole;
}
function computeTrusteeFee(saleValue) {
    if (saleValue <= 5000) {
      return saleValue * 0.25;
    } else if (saleValue <= 50000) {
      return 5000 * 0.25 + (saleValue - 5000) * 0.10;
    } else if (saleValue <= 1000000) {
      return 5000 * 0.25 + (50000 - 5000) * 0.10 + (saleValue - 50000) * 0.05;
    } else {
      return (
        5000 * 0.25 +
        (50000 - 5000) * 0.10 +
        (1000000 - 50000) * 0.05 +
        (saleValue - 1000000) * 0.03
      );
    }
}

function computeLiquidationForProperty(property, answers) {
    let saleValue = Number(property.value);
    if (isNaN(saleValue)) {
      return {
        label: property.label,
        error: "Invalid sale value",
        liquidationValue: -Infinity,
      };
    }
    if (answers.homeOwnershipType ==="My spouse owns" && answers.spouseFiling ==="no") {
      saleValue = 0;
    }
    // Sales commission is 6% of saleValue.
    const salesCommission = saleValue * 0.06;
  
    // Trustee fee is calculated in tiers.
    const trusteeFee = computeTrusteeFee(saleValue);
  
    // For the primary home, apply the homestead exemption.
    let exemption = 0;
    if (property.isPrimary) {
      exemption = getHomesteadExemption(
        exemptionData,
        property.state,
        answers.maritalStatus,
        answers.homeOwnershipType
      );
    }
    // For other real estate, no homestead exemption is applied.
    // (If you have other types of exemptions, you can add logic here.)
  
    // Sum up the mortgage balances (if any).
    const totalMortgage =
      Number(property.mortgageBalance || 0) + Number(property.secondMortgageBalance || 0);
  
    // Liquidation value: saleValue minus all deductions.
    let liquidationValue = saleValue - salesCommission - trusteeFee - exemption - totalMortgage;
    if (liquidationValue < 0){
        liquidationValue = 0;
    }
  
    return {
      label: property.label,
      saleValue,
      salesCommission,
      trusteeFee,
      exemption,
      totalMortgage,
      liquidationValue,
    };
}
// Helper: Compute liquidation value for the home.
// Uses sales commission (6%), trustee fee (tiered), homestead exemption, and current mortgage balances.
const computeLiquidationValue = (answers) => {
    const analyses = [];

    // Respondent primary home
    if (answers.ownsHome === "yes") {
      const primaryHome = {
        label: "Primary Home",
        value: answers.homeValue,
        mortgageBalance: answers.mortgageBalance,
        secondMortgageBalance: answers.secondMortgageBalance,
        state: answers.state, // convert abbreviation to full name
        isPrimary: true,
      };
      analyses.push(computeLiquidationForProperty(primaryHome, answers));
    }
  
    // Respondent other real estate
    if (answers.hasOtherRealEstate === "yes" && Array.isArray(answers.otherRealEstateProperties)) {
      answers.otherRealEstateProperties.forEach((prop, idx) => {
        const property = {
          label: `Other Real Estate ${idx + 1} (Respondent)`,
          value: prop.value,
          mortgageBalance: prop.mortgageBalance,
          secondMortgageBalance: prop.secondMortgageBalance,
          state: prop.state || getStateName(answers.state),
          isPrimary: false,
        };
        analyses.push(computeLiquidationForProperty(property, answers));
      });
    }
  
    // Spouse other real estate (if spouse is filing)
    if (
      answers.maritalStatus === "married" &&
      answers.spouseFiling === "yes" &&
      answers.spouseHasOtherRealEstate === "yes" &&
      Array.isArray(answers.spouseOtherRealEstateProperties)
    ) {
      answers.spouseOtherRealEstateProperties.forEach((prop, idx) => {
        const property = {
          label: `Other Real Estate ${idx + 1} (Spouse)`,
          value: prop.value,
          mortgageBalance: prop.mortgageBalance,
          secondMortgageBalance: prop.secondMortgageBalance,
          state: prop.state || getStateName(answers.spouseState || answers.state),
          isPrimary: false,
        };
        analyses.push(computeLiquidationForProperty(property, answers));
      });
    }
  
    return analyses;
};

// Helper: Check if any vehicle (that the debtor wants to keep) is more than 2 months behind.
const checkRepoForeclosure = (answers) => {

  // For vehicles:
  if (answers.hasVehicle === "yes" && Array.isArray(answers.vehicles)) {
    for (const vehicle of answers.vehicles) {
      if (vehicle.keep === "keep" && vehicle.currentOnPayments === "no" && Number(vehicle.monthsBehind) >= 2) {
        return true;
      }
    }
  }
  // For home: if mortgage payment status is "no" and months behind is 2 or more.
  if (answers.ownsHome === "yes" && answers.hasMortgage === "yes") {
    if (answers.mortgagePaymentStatus === "no" && Number(answers.mortgageMonthsBehind) >= 2) {
      return true;
    }
  }
  return false;
};

const applyChapter13PaymentOverride = (plan, rawOverride) => {
  if (rawOverride === null || rawOverride === undefined) return plan;
  const normalized = String(rawOverride).replace(/[$,]/g, '').trim();
  if (!normalized) return plan;

  const override = Number(normalized);
  if (!Number.isFinite(override) || override < 0) return plan;

  return {
    ...plan,
    autoCalculatedMonthlyPayment: plan.monthlyPayment,
    monthlyPayment: override,
    monthlyPaymentOverridden: true,
  };
};

// Main function that combines everything
const computeBankruptcyConsultationResult = (answers) => {
  const attorneyFeeSettings = resolveAttorneyFeeSettings(answers);
  // Step A: Discharge eligibility.
  const planLength = 60
  const plan60 = applyChapter13PaymentOverride(
    computeChapter13Plan(answers, planLength),
    answers.override_chapter_13_payment_60
  );
  const planLength36 = 36
  const plan36 = applyChapter13PaymentOverride(
    computeChapter13Plan(answers, planLength36),
    answers.override_chapter_13_payment_36
  );
  //const disposableIncomeResults = computeDisposableIncome(answers);

  //console.log(plan60)
  //console.log(plan36)
  const dischargeEligible = checkDischargeEligibility(answers);


  // Step B: Means test.
  
  const {medianIncome,incomeTotals, householdSize} = passesMeansTest(answers);
  console.log("median income: " + medianIncome)
  console.log(incomeTotals)
  //console.log(incomeTotals)
  const aboveMedianIncome = ((incomeTotals.totalIncome * 12) > medianIncome);
  // Annualized income and sliding threshold based on household size:
  const annualIncome = incomeTotals.totalIncome * 12;
  const slidingPercent = 11 - householdSize; // size 1 => 10%, size 2 => 9%, ..., size 11 => 0%
  const threshold = medianIncome * (1 + slidingPercent / 100);
  const longFormEligible = annualIncome <= threshold;


  // Step C: Liquidation limitation.
  const analyses = computeLiquidationValue(answers);

  const totalLiquidationValue = analyses.reduce((sum, analysis) => {
    return sum + (Number(analysis.liquidationValue) || 0);
    }, 0);
  const liquidationInChapter7 = totalLiquidationValue > 0


  // Step D: Repo/foreclosure check.
  const repoOrForeclosure = checkRepoForeclosure(answers);


  // If all checks pass, compute fees.
  if (!repoOrForeclosure && !liquidationInChapter7 && longFormEligible && dischargeEligible){
    let attorneyFee = attorneyFeeSettings.base_chapter_7;
    if (answers.maritalStatus === "married" && answers.spouseFiling === "yes") {
      attorneyFee += attorneyFeeSettings.chapter_7_joint_additional;
    }
    // Add $50 for each vehicle that has a loan.

    if (answers.payType === "self-employed") {
      attorneyFee += attorneyFeeSettings.chapter_7_self_employed_additional;
    }
    if (answers.maritalStatus === "married" && answers.spouseFiling === "yes" && answers.spousePayType === "self-employed") {
      attorneyFee += attorneyFeeSettings.chapter_7_self_employed_additional;
    }
    // Add $50 for each vehicle that is a loan and being kept
    if (Array.isArray(answers.vehicles)) {
      for (const vehicle of answers.vehicles) {
        if ((vehicle.loan === 'loan' || vehicle.loanBalance != '') && vehicle.keep === 'keep') {
          attorneyFee += attorneyFeeSettings.chapter_7_reaffirmation_additional;
        }
      }
    }
    if (Array.isArray(answers.spouseVehicles)) {
      for (const vehicle of answers.spouseVehicles) {
        if ((vehicle.loan === 'loan' || vehicle.loanBalance != '') && vehicle.keep === 'keep') {
          attorneyFee += attorneyFeeSettings.chapter_7_reaffirmation_additional;
        }
      }
    }
    if (answers.ownsHome === 'yes'){
      attorneyFee += attorneyFeeSettings.chapter_7_reaffirmation_additional
    }
    if (longFormEligible && aboveMedianIncome){
      attorneyFee += 200
    }

    const filingFee = 338;
    const creditCounseling = 24; // $12 pre, $12 post

    return {
      recommendation: "Chapter 7 recommended.",
      attorneyFeeSchedule: attorneyFeeSettings,
      fees: {
        attorneyFee,
        totalAttorneyFee: attorneyFee,
        filingFee,
        creditCounseling,
        total: attorneyFee + filingFee + creditCounseling
      },
      computedValues: {
        incomeTotals,
        longFormEligible,
        analyses,
        plan36,
        medianIncome,
        plan60
      }
    };
  } else {
    let attorneyFee = 500;
    if (answers.ownsHome === 'yes'){
      attorneyFee = 750;
    }
    if (aboveMedianIncome){
      attorneyFee = 1000;
    }
    const filingFee = 313;
    const creditCounseling = 24; // $12 pre, $12 post
    const totalAttorneyFee = getChapter13TotalAttorneyFee(answers);
    return {
      recommendation: "Chapter 13 recommended.",
      attorneyFeeSchedule: attorneyFeeSettings,
      fees: {
        attorneyFee,
        totalAttorneyFee,
        filingFee,
        creditCounseling,
        total: attorneyFee+filingFee+creditCounseling
      },
      computedValues: {
        incomeTotals,
        medianIncome,
        analyses,
        longFormEligible,
        liquidationInChapter7,
        dischargeEligible,
        aboveMedianIncome,
        repoOrForeclosure,
        plan60,
        plan36
      }
    }
  }


}

export {
    checkDischargeEligibility,
    getSixMonthPeriod,
    computeJobIncome,
    computePriorJobIncome,
    computeTotalMonthlyIncome,
    getMedianIncome,
    passesMeansTest,
    getHomesteadExemption,
    computeLiquidationValue,
    checkRepoForeclosure,
    computeBankruptcyConsultationResult
  };
